import {useQuery} from "@tanstack/react-query";
import {doc, getDoc} from "firebase/firestore";
import type {ZodType, z} from "zod/v4";

import {BaseCollectionConfig} from "~/api/BaseCollectionConfig";
import {firestore} from "~/firebase/firebase";

import {queryClient} from "../queryClient";

// Options for the getById hook
export interface GetByIdOptions {
  // Whether to wait for realtime updates or fetch directly when not cached
  waitForRealtimeUpdates?: boolean;
}

// This factory focuses on data retrieval with options
export const getByIdHookFactory = <
  T extends ZodType,
  CollectionArgs extends NonNullable<unknown>,
>(
  config: BaseCollectionConfig<T, CollectionArgs>,
) => {
  return (key: CollectionArgs & {id?: string}, options?: GetByIdOptions) => {
    const waitForUpdates = !!options?.waitForRealtimeUpdates;

    return useQuery<z.infer<T>>({
      queryKey: config.getDocumentPath(key),
      queryFn: async () => {
        // Check for cached data from subscription
        const cachedData = queryClient.getQueryData<z.infer<T>>(
          config.getDocumentPath(key),
        );

        // If we have cached data or are waiting for updates, return the cached data
        if (cachedData) {
          return config.parseData(key.id ?? "", cachedData);
        }

        // If not waiting for realtime updates or no cached data, fetch directly
        if (!waitForUpdates) {
          const myDoc = await getDoc(
            doc(firestore, ...config.getDocumentPath(key)),
          );
          return config.parseData(myDoc.id, myDoc.data());
        }

        throw new Error("Waiting for realtime updates");
      },
      enabled: !Object.values(key).some((segment) => !segment),
      staleTime: Number.POSITIVE_INFINITY, // Data stays fresh due to either manual fetching or real-time updates
    });
  };
};
